import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { fieldSx } from './PartsShipmentsHelpers';
import { LogTypeNames } from '../../../../../configs/enums';
import { useSearchLinkShipments } from '../../../../../queries/live-search-query';
import { useCreateShipmentServiceAllocation } from '../../../../../queries/shipment-query';

export interface LinkShipmentPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  serviceId: number;
  clientHierarchy?: string;
}

export const LinkShipmentPopover: React.FC<LinkShipmentPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  serviceId,
  clientHierarchy,
}) => {
  const [searchId, setSearchId] = useState('');
  const searchMutation = useSearchLinkShipments();
  const linkMutation = useCreateShipmentServiceAllocation();
  const [results, setResults] = useState<Array<Record<string, unknown>>>([]);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const handleSearch = useCallback(async () => {
    if (!searchId.trim()) return;
    setError('');
    try {
      const data = await searchMutation.mutateAsync({
        id: searchId.trim(),
        clientHierarchy: clientHierarchy ?? '',
      });
      setResults(data as unknown as Array<Record<string, unknown>>);
      if (!data?.length) setError('No shipment found with that ID');
    } catch {
      setError('Search failed');
    }
  }, [searchId, clientHierarchy, searchMutation]);

  const handleLink = useCallback(
    async (shipmentId: number) => {
      try {
        await linkMutation.mutateAsync({
          shipmentId,
          serviceId,
          logType: LogTypeNames.ExistingShipmentLinked,
        });
        onClose();
      } catch {
        setError('Failed to link — it may already be linked');
      }
    },
    [serviceId, linkMutation, onClose]
  );

  return (
    <Popper open={open} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1500 }}>
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={8}
          sx={{
            width: 380,
            p: 2,
            mt: 0.5,
            border: '1px solid',
            borderColor: 'v4.border',
            borderRadius: 2,
          }}
        >
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>
            Link Existing Shipment
          </Typography>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 1 }}>
            Enter a Shipment ID (e.g. 12345 or AM-SID-12345)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <InputBase
              placeholder="Shipment ID..."
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              inputRef={inputRef}
              sx={{ ...fieldSx, flex: 1 }}
              inputProps={{ 'aria-label': 'Shipment ID' }}
            />
            <Box
              component="button"
              onClick={handleSearch}
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                border: 'none',
                borderRadius: '4px',
                bgcolor: 'v4.accent',
                color: 'common.white',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {searchMutation.isPending ? '...' : 'Search'}
            </Box>
          </Box>
          {error && (
            <Typography sx={{ fontSize: '11px', color: 'error.main', mb: 0.5 }}>{error}</Typography>
          )}
          {results.length > 0 && (
            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {results.map((r) => (
                <Box
                  key={String(r.id)}
                  onClick={() => handleLink(Number(r.id))}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.75,
                    px: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'v4.tableHover' },
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                      AM-SID-{String(r.id)}
                    </Typography>
                    <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                      {String(r.source ?? '-')} → {String(r.destination ?? '-')}
                    </Typography>
                  </Box>
                  {linkMutation.isPending && <CircularProgress size={14} />}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};
